import React, { useState } from 'react';
import { PageHeader, useReveal, GlowDivider } from '../components/Shared';

const TEAM = [
  { name:'Dr. Sarah Chen',      role:'Principal Investigator',     dept:'Lab Director',       color:'teal',   bio:'PhD MIT · 15 yrs materials AI · 120+ publications · NSF CAREER Award', initials:'SC', areas:['AI/ML','Battery Materials','Scale-Up'] },
  { name:'Dr. Ravi Patel',      role:'Sr. Research Scientist',     dept:'Computational',      color:'purple', bio:'PhD Stanford · DFT & molecular dynamics · Former Google Brain researcher', initials:'RP', areas:['DFT','Neural Potentials','HPC'] },
  { name:'Dr. Oluwaseun Adesa', role:'Research Scientist',         dept:'AI & Informatics',   color:'pink',   bio:'PhD Caltech · Bayesian optimization · Graph neural networks for materials', initials:'OA', areas:['Graph NNs','Bayesian Opt','Generative AI'] },
  { name:'Dr. Hiroshi Tanaka',  role:'Research Scientist',         dept:'Robotics & Fab',     color:'teal',   bio:'PhD U Tokyo · Autonomous synthesis · Lab automation · Robotics systems', initials:'HT', areas:['Robotics','Synthesis','Automation'] },
  { name:'Dr. Amara Liu',       role:'Postdoctoral Researcher',    dept:'Electrochemistry',   color:'purple', bio:'PhD Princeton · Electrochemical characterization · Battery cycling', initials:'AL', areas:['Electrochemistry','Testing','BMS'] },
  { name:'Marcus Johnson',      role:'Research Engineer',          dept:'Software & Data',    color:'pink',   bio:'MS CMU · Full-stack lab software · Data pipelines · Digital twins', initials:'MJ', areas:['Software','Data Eng','Digital Twin'] },
  { name:'Dr. Priya Sharma',    role:'Postdoctoral Researcher',    dept:'Computational',      color:'teal',   bio:'PhD Oxford · Phase-field modeling · Microstructure simulation', initials:'PS', areas:['Phase-Field','FEA','Microstructure'] },
  { name:'Wei Zhang',           role:'PhD Student',                dept:'Materials Science',  color:'purple', bio:'3rd year · Perovskite photovoltaics · Autonomous synthesis protocols', initials:'WZ', areas:['Perovskites','Solar','Synthesis'] },
  { name:'Fatima Al-Hassan',    role:'PhD Student',                dept:'Data Science',       color:'pink',   bio:'2nd year · Transfer learning · Materials property prediction', initials:'FA', areas:['Transfer Learning','NLP','Property Pred'] },
];

const colorMap  = { teal:'#00ffff', purple:'#a78bfa', pink:'#f9a8d4' };
const bgMap     = { teal:'rgba(0,255,255,0.07)', purple:'rgba(139,92,246,0.08)', pink:'rgba(244,114,182,0.07)' };
const borderMap = { teal:'rgba(0,255,255,0.18)', purple:'rgba(139,92,246,0.2)', pink:'rgba(244,114,182,0.18)' };

const DEPTS = ['All','Lab Director','Computational','AI & Informatics','Robotics & Fab','Electrochemistry','Software & Data','Materials Science','Data Science'];

export default function Team() {
  useReveal();
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? TEAM : TEAM.filter(m => m.dept === filter);

  return (
    <>
      <PageHeader
        tag="Our Team"
        title="The Minds Behind"
        accent="D2S"
        sub="A world-class team of researchers, engineers, and scientists united by one mission: accelerating materials innovation."
      />

      <GlowDivider />

      {/* Filter bar */}
      <section className="section">
        <div className="container">
          <div className="flex flex-wrap gap-2 justify-center mb-12 reveal">
            {DEPTS.map(d => (
              <button key={d} onClick={() => setFilter(d)}
                className="font-mono text-xs tracking-[2px] uppercase px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer"
                style={{
                  fontFamily:'var(--font-mono)',
                  background: filter===d ? 'rgba(0,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                  color:      filter===d ? '#00ffff' : 'var(--text-2)',
                  borderColor:filter===d ? 'rgba(0,255,255,0.25)' : 'var(--border)',
                }}>
                {d}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((member, i) => (
              <div key={member.name} className="glass-card reveal group cursor-default"
                style={{padding:'36px 32px', transitionDelay:`${i*0.07}s`, position:'relative'}}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{background:`linear-gradient(90deg,transparent,${colorMap[member.color]},transparent)`}} />

                <div className="flex items-center gap-4 mb-6">
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-full flex items-center justify-center font-display font-bold text-lg flex-shrink-0"
                    style={{background:bgMap[member.color],color:colorMap[member.color],border:`2px solid ${borderMap[member.color]}`,boxShadow:`0 0 20px ${bgMap[member.color]}`}}>
                    {member.initials}
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base tracking-[-0.02em] leading-tight">{member.name}</h3>
                    <p className="text-xs mt-0.5" style={{color:colorMap[member.color],fontFamily:'var(--font-mono)'}}>{member.role}</p>
                  </div>
                </div>

                <span className="inline-block font-mono text-[0.6rem] tracking-[2px] uppercase px-2.5 py-1 rounded-full mb-4"
                  style={{color:colorMap[member.color],background:bgMap[member.color],border:`1px solid ${borderMap[member.color]}`}}>
                  {member.dept}
                </span>

                <p className="text-xs font-light leading-relaxed mb-5" style={{color:'var(--text-2)'}}>{member.bio}</p>

                <div className="flex flex-wrap gap-2">
                  {member.areas.map(a => (
                    <span key={a} className="text-[0.65rem] px-2 py-0.5 rounded font-mono" style={{background:'rgba(255,255,255,0.04)',color:'var(--text-3)',border:'1px solid var(--border)'}}>
                      {a}
                    </span>
                  ))}
                </div>

                {/* Social icons row */}
                <div className="flex gap-3 mt-5 pt-5 border-t" style={{borderColor:'var(--border)'}}>
                  {['Scholar','LinkedIn','GitHub'].map(s => (
                    <a key={s} href="#" className="font-mono text-[0.6rem] tracking-wide uppercase transition-colors duration-300"
                      style={{color:'var(--text-3)'}}
                      onMouseEnter={e=>e.currentTarget.style.color=colorMap[member.color]}
                      onMouseLeave={e=>e.currentTarget.style.color='var(--text-3)'}>
                      {s}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Join the team CTA */}
      <section className="section section-alt">
        <div className="container">
          <div className="glass-card reveal text-center p-16" style={{position:'relative'}}>
            <div className="absolute inset-0 pointer-events-none rounded-[18px]"
              style={{background:'radial-gradient(ellipse at center,rgba(0,255,255,0.04) 0%,transparent 70%)'}} />
            <span className="section-tag justify-center">Join the Lab</span>
            <h2 className="section-title mb-4">We're <span className="text-gradient">Hiring</span></h2>
            <p className="text-sm font-light leading-relaxed mx-auto mb-10 max-w-[480px]" style={{color:'var(--text-2)'}}>
              We're always looking for exceptional researchers to join our team. If you're passionate about accelerating materials science, we'd love to hear from you.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="/careers" className="btn btn--primary">View Open Roles</a>
              <a href="/contact" className="btn btn--glass">Send Your CV</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
